import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from 'sonner';
import { Bell, Send, Users, Smartphone, RefreshCw, CheckCircle, AlertCircle } from 'lucide-react';
import api from '../../lib/api';
import { isAdminLikeRole } from '../../lib/roles';

const AdminPushNotifications = () => {
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [notification, setNotification] = useState({
    title: '',
    body: '',
  });
  const [sending, setSending] = useState(false);
  const [stats, setStats] = useState({
    total_devices: 0,
    active_users: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load users (push notification recipients — need everyone)
      const usersRes = await api.get('/users', { params: { limit: 5000 } });
      setUsers(usersRes.data || []);

      // Calculate stats from users with push tokens
      const devicesRes = await api.get('/admin/push-stats');
      if (devicesRes.data) {
        setStats(devicesRes.data);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectUser = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAll = () => {
    if (selectedUsers.length === users.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(users.map(u => u.id));
    }
  };

  const handleSelectByRole = (role) => {
    const roleUsers = users.filter(u => u.role === role).map(u => u.id);
    setSelectedUsers(roleUsers);
  };

  const handleSendNotification = async () => {
    if (!notification.title || !notification.body) {
      toast.error('Please enter both title and message');
      return;
    }

    if (selectedUsers.length === 0) {
      toast.error('Please select at least one user');
      return;
    }

    setSending(true);
    try {
      await api.post('/push/send', {
        user_ids: selectedUsers,
        title: notification.title,
        body: notification.body,
      });
      
      toast.success(`Notification sent to ${selectedUsers.length} users`);
      setNotification({ title: '', body: '' });
      setSelectedUsers([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send notification');
    } finally {
      setSending(false);
    }
  };

  const getStatusBadge = (firebaseEnabled) => {
    if (firebaseEnabled) {
      return (
        <Badge className="bg-green-100 text-green-700 flex items-center gap-1">
          <CheckCircle className="h-3 w-3" />
          Active
        </Badge>
      );
    }
    return (
      <Badge variant="secondary" className="flex items-center gap-1">
        <AlertCircle className="h-3 w-3" />
        Not Configured
      </Badge>
    );
  };

  return (
    <div className="space-y-6" data-testid="admin-push-notifications">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <Bell className="h-6 w-6 text-[#4E50FF]" />
          Push Notifications
        </h1>
        <p className="text-slate-500 mt-1">Send push notifications to mobile app users</p>
      </div>

      {/* Status Cards */}
      <div className="grid md:grid-cols-3 gap-4">
        <Card className="border shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Firebase Status</p>
                <div className="mt-1">{getStatusBadge(true)}</div>
              </div>
              <div className="h-12 w-12 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Registered Devices</p>
                <p className="text-2xl font-bold text-slate-900">{stats.total_devices || 0}</p>
              </div>
              <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center">
                <Smartphone className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Users</p>
                <p className="text-2xl font-bold text-slate-900">{users.length}</p>
              </div>
              <div className="h-12 w-12 bg-purple-100 rounded-full flex items-center justify-center">
                <Users className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Compose Notification */}
        <Card className="border shadow-sm">
          <CardHeader>
            <CardTitle className="text-base font-medium flex items-center gap-2">
              <Send className="h-4 w-4" />
              Compose Notification
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-slate-600">Title</Label>
              <Input
                value={notification.title}
                onChange={(e) => setNotification(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Notification title..."
                className="mt-1"
              />
            </div>

            <div>
              <Label className="text-slate-600">Message</Label>
              <Textarea
                value={notification.body}
                onChange={(e) => setNotification(prev => ({ ...prev, body: e.target.value }))}
                placeholder="Notification message..."
                className="mt-1 h-24"
              />
            </div>

            <div className="bg-slate-50 p-3 rounded-lg">
              <p className="text-sm text-slate-600 mb-2">Quick Select:</p>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleSelectAll}
                  className="text-xs"
                >
                  {selectedUsers.length === users.length ? 'Deselect All' : 'Select All'}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleSelectByRole('agent')}
                  className="text-xs"
                >
                  All Agents
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleSelectByRole('customer')}
                  className="text-xs"
                >
                  All Customers
                </Button>
              </div>
            </div>

            <Button
              onClick={handleSendNotification}
              disabled={sending || selectedUsers.length === 0}
              className="w-full bg-[#4E50FF] hover:bg-[#4E50FF]/90"
            >
              {sending ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Send to {selectedUsers.length} Users
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* User Selection */}
        <Card className="border shadow-sm">
          <CardHeader>
            <CardTitle className="text-base font-medium flex items-center gap-2">
              <Users className="h-4 w-4" />
              Select Recipients ({selectedUsers.length} selected)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center h-48">
                <RefreshCw className="h-6 w-6 animate-spin text-slate-400" />
              </div>
            ) : (
              <div className="max-h-80 overflow-y-auto space-y-2">
                {users.map((user) => (
                  <div
                    key={user.id}
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                      selectedUsers.includes(user.id)
                        ? 'border-[#4E50FF] bg-[#4E50FF]/5'
                        : 'border-transparent hover:bg-slate-50'
                    }`}
                    onClick={() => handleSelectUser(user.id)}
                  >
                    <Checkbox
                      checked={selectedUsers.includes(user.id)}
                      onCheckedChange={() => handleSelectUser(user.id)}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-900 text-sm truncate">
                        {user.full_name || user.email}
                      </p>
                      <p className="text-xs text-slate-500 truncate">{user.email}</p>
                    </div>
                    <Badge
                      variant="secondary"
                      className={`text-xs ${
                        isAdminLikeRole(user.role) ? 'bg-purple-100 text-purple-700' :
                        user.role === 'agent' ? 'bg-blue-100 text-blue-700' :
                        'bg-slate-100 text-slate-700'
                      }`}
                    >
                      {user.role}
                    </Badge>
                  </div>
                ))}

                {users.length === 0 && (
                  <div className="text-center py-8 text-slate-500">
                    <Users className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>No users found</p>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminPushNotifications;
